import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

export interface SimpleCoordinates {
  latitude: number;
  longitude: number;
}

export interface GeolocationFailure extends Error {
  code?: number;
}

const PERMISSION_DENIED = 1;
const POSITION_UNAVAILABLE = 2;
const TIMEOUT = 3;

function failure(message: string, code?: number): GeolocationFailure {
  const error: GeolocationFailure = new Error(message);
  error.code = code;
  return error;
}

// Resolves the device's current coordinates using the native Capacitor
// Geolocation plugin when running as an Android app, falling back to the
// browser Geolocation API when running as a regular web page.
export async function getCurrentCoordinates(options?: {
  enableHighAccuracy?: boolean;
  timeout?: number;
}): Promise<SimpleCoordinates> {
  if (Capacitor.isNativePlatform()) {
    let permission = await Geolocation.checkPermissions();
    if (permission.location !== 'granted') {
      permission = await Geolocation.requestPermissions();
    }
    if (permission.location !== 'granted') {
      throw failure('geolocation_permission_denied', PERMISSION_DENIED);
    }

    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: options?.enableHighAccuracy ?? true,
        timeout: options?.timeout ?? 15000,
      });
      return { latitude: position.coords.latitude, longitude: position.coords.longitude };
    } catch (error) {
      throw failure('geolocation_position_unavailable', POSITION_UNAVAILABLE);
    }
  }

  if (!navigator.geolocation) {
    throw failure('geolocation_not_supported');
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
      (error) => reject(failure(error.message, error.code)),
      { enableHighAccuracy: options?.enableHighAccuracy ?? true, timeout: options?.timeout ?? 15000 }
    );
  });
}

export type GeolocationPermissionState = 'granted' | 'denied' | 'prompt' | 'unknown';

// Mirrors navigator.permissions.query({ name: 'geolocation' }).state, but
// reads from the native permission state when running as an Android app.
export async function checkGeolocationPermission(): Promise<GeolocationPermissionState> {
  if (Capacitor.isNativePlatform()) {
    try {
      const result = await Geolocation.checkPermissions();
      if (result.location === 'granted') return 'granted';
      if (result.location === 'denied') return 'denied';
      return 'prompt';
    } catch {
      return 'unknown';
    }
  }

  if (navigator.permissions && navigator.permissions.query) {
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      return status.state as GeolocationPermissionState;
    } catch {
      return 'unknown';
    }
  }
  return 'unknown';
}

export const GEOLOCATION_ERROR_CODES = { PERMISSION_DENIED, POSITION_UNAVAILABLE, TIMEOUT };
